import { useRef, useEffect, useState, useCallback } from "react";
import Cleave from "cleave.js/react";
import { tw } from "@careinfo/tailwind";
import { toast } from "react-toastify";

const InputCell = ({
  row,
  getValue,
  dataKey,
  searchParams,
  setSearchParams,
  table,
  column,
}) => {
  const cleaveRef = useRef<any>(null);
  const id = row?.original?.id;
  const paramKey = `${id}:${dataKey}`;

  const getParamValue = () => {
    const edited = searchParams.get("editedOrders");
    if (!edited) return null;

    const match = edited
      .split(",")
      .find((entry) => entry.startsWith(`${paramKey}=`));
    return match ? match.split("=")[1] : null;
  };

  const paramValue = getParamValue();
  const initialValue = paramValue ?? getValue()?.toString() ?? "";

  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    const updatedParam = getParamValue();
    const fallback = getValue()?.toString() ?? "";
    setValue(updatedParam ?? fallback);
  }, [searchParams, row?.original?.id, dataKey]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value.toString());
  };

  const handleBlur = () => {
    if (
      Number(value) === 0 &&
      dataKey !== "delivery_charge" &&
      dataKey !== "branch_charge" &&
      dataKey !== "flat_amount"
    ) {
      toast.error("Value can't be zero!");
      setValue(initialValue);
      return;
    }

    const updateKey = `${paramKey}=${value}`;
    const newParams = new URLSearchParams(searchParams.toString());

    let updates = newParams.get("editedOrders")?.split(",") ?? [];
    updates = updates.filter((entry) => !entry.startsWith(`${paramKey}=`));
    updates.push(updateKey);

    newParams.set("editedOrders", updates.join(","));
    setSearchParams(newParams);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") {
      const allColumns = table.getAllLeafColumns();
      const editableColumns = allColumns.filter(
        (col) => col.columnDef?.meta?.isEditable
      );

      const currentColIndex = editableColumns.findIndex(
        (col) => col.id === column.id
      );
      const allRows = table.getRowModel().rows;
      const currentRowIndex = allRows.findIndex(
        (r) => r.original === row.original
      );

      if (currentColIndex === -1 || currentRowIndex === -1) return;

      if (e.shiftKey) {
        if (currentColIndex > 0) {
          focusCell(currentRowIndex, editableColumns[currentColIndex - 1].id);
        } else if (currentRowIndex > 0) {
          focusCell(
            currentRowIndex - 1,
            editableColumns[editableColumns.length - 1].id
          );
        }
      } else {
        if (currentColIndex < editableColumns.length - 1) {
          focusCell(currentRowIndex, editableColumns[currentColIndex + 1].id);
        } else if (currentRowIndex < allRows.length - 1) {
          focusCell(currentRowIndex + 1, editableColumns[0].id);
        }
      }
    }
  };

  const focusCell = (rowIndex: number, columnId: string, attempts = 10) => {
    if (attempts <= 0) return;

    requestAnimationFrame(() => {
      const selector = `[data-row-index="${rowIndex}"][data-col-id="${columnId}"] input`;
      const input = document.querySelector(selector) as HTMLInputElement;

      if (input) {
        input.focus();
        input.select();
      } else {
        focusCell(rowIndex, columnId, attempts - 1);
      }
    });
  };

  return (
    <div
      data-row-index={row.index}
      data-col-id={column.id}
      className={tw`px-2`}
    >
      <Cleave
        type="text"
        className={tw`px-2 w-32 bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
        value={value || 0}
        autoComplete="off"
        name={dataKey}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        ref={cleaveRef}
        options={{
          numeral: true,
          numeralDecimalMark: ".",
          numeralDecimalScale: 2,
          numeralPositiveOnly: true,
          delimiter: "",
        }}
      />
    </div>
  );
};

export default InputCell;
